import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { Audit } from 'types/audit_types';
import Text from 'components/Text';
import { cn } from 'lib/utils';
import { ReactSVG } from 'react-svg';
import { edit_icon, delete_icon, view_icon } from 'asset/icons';
import { format } from 'date-fns';

interface Props {
  audits: Audit[];
  onEdit: (audit: Audit) => void;
  onView?: (audit: Audit) => void;
  onDelete?: (audit: Audit) => void;
}

const AuditTable = ({ audits, onEdit, onView, onDelete }: Props) => {
  return (
    <div className='min-h-0'>
      <div className='rounded-xl border max-h-full overflow-y-auto'>
        <Table>
          <TableHeader>
            <TableRow className='hover:bg-transparent'>
              <TableHead className='px-4 text-black py-3 min-w-24'>Audit ID</TableHead>
              <TableHead className='px-4 text-black py-3 min-w-24'>Audit Name</TableHead>
              <TableHead className='text-black min-w-32'>Customer Name</TableHead>
              <TableHead className='text-black min-w-28'>Auditors</TableHead>
              <TableHead className='text-black min-w-24'>Start Date</TableHead>
              <TableHead className='text-black min-w-24'>Status</TableHead>
              <TableHead className='text-black'>Action</TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {audits?.map((audit) => {
              const auditors = audit?.users?.length > 0 ? audit?.users?.map((user) => user?.fullName)?.join(', ') : '-';
              const is_completed = audit?.auditStatusId == 1
              return (
                <TableRow key={audit?.auditId} className='hover:bg-muted/50'>
                  {/* Audit ID */}
                  <TableCell className='px-4 py-3'>
                    <Text text={`Audit# ${audit?.auditId ?? ''}`} className='text-muted-foreground' />
                  </TableCell>


                  {/* Audit Name */}
                  <TableCell className='px-4 py-3'>
                    <Text text={audit?.auditName ?? '-'} className='text-muted-foreground' />
                  </TableCell>

                  {/* Customer */}
                  <TableCell>
                    <Text text={audit?.customerName ?? '-'} className='text-muted-foreground' />
                  </TableCell>

                  {/* Auditor */}
                  <TableCell>
                    <Text text={auditors ?? '-'} className='text-muted-foreground' />
                  </TableCell>

                  {/* Start Date */}
                  <TableCell>
                    <Text text={audit?.startTime ? format(new Date(audit?.startTime), 'dd/MM/yyyy') : '-'} className='text-muted-foreground' />
                  </TableCell>

                  {/* Status */}
                  <TableCell>
                    <span
                      className={cn(
                        'inline-flex items-center px-3 py-1 rounded-full text-xs font-medium',
                        is_completed ? 'bg-primary text-white' : 'bg-primary/20 text-primary',
                      )}
                    >
                      {is_completed ? 'Completed' : audit?.auditStatusId == 2 ? 'Pending' : 'In Progress'}
                    </span>
                  </TableCell>

                  {/* Actions */}
                  <TableCell>
                    <div className='flex gap-4'>
                      <div className='flex items-center gap-1 cursor-pointer' onClick={() => onView?.(audit)}>
                        <Text text='View' className='text-muted-foreground' />
                        <ReactSVG src={view_icon} />
                      </div>
                      {
                        !is_completed &&
                        <div className='flex items-center gap-1 cursor-pointer' onClick={() => onEdit(audit)}>
                          <Text text='Edit' className='text-muted-foreground' />
                          <ReactSVG src={edit_icon} />
                        </div>
                      }
                      <div className='flex items-center gap-1 cursor-pointer text-destructive' onClick={() => onDelete?.(audit)}>
                        <Text text='Delete' className='text-destructive' />
                        <ReactSVG src={delete_icon} />
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}

            {audits?.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className='py-10 text-center'>
                  <Text text='No audits found.' className='text-muted-foreground' />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default AuditTable;
